/**
 * <RaidBanner> — sticky alert bar that appears on every route when at
 * least one raid boss is currently active on either server.
 *
 * Polls `GET /api/raids/active` every 60 s (cheap query, indexed on
 * `status='active'`). When the list is non-empty we cycle the visible
 * raid every 8 s so multi-server raids both get airtime.
 *
 * Dismissal: clicking the × hides the banner for the *current* session
 * (sessionStorage) — it'll come back next reload. We deliberately do
 * NOT use localStorage because raids are time-limited and users miss
 * out if they dismiss permanently.
 *
 * Click anywhere else on the banner → routes to the LiveMap so the
 * hunter can see exactly where to go.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Swords, X, MapPin } from "lucide-react";
import { api } from "@/lib/api";

const POLL_MS = 60_000;
const ROTATE_MS = 8_000;

const CATEGORY_STYLES = {
  combat: { color: "from-red-700/80 to-rose-900/80", chip: "⚔️", label: "COMBAT" },
  speed:  { color: "from-blue-700/80 to-cyan-900/80", chip: "💨", label: "SPEED" },
  work:   { color: "from-amber-700/80 to-orange-900/80", chip: "⚒️", label: "WORK" },
};

// Discord application-emoji IDs — same set used by the bot's embeds.
// Kept here so the banner can pull the icon straight from Discord's
// CDN instead of bundling a second copy. See PalAssets.jsx for the
// canonical version of this map.
const ELEMENT_EMOJI_IDS = {
  fire:     "1485468512897073202",
  water:    "1485468513907773450",
  electric: "1485468514541240540",
  grass:    "1485468515128574074",
  ground:   "1485468515724034215",
  ice:      "1485468516986650675",
  dark:     "1485468517653413908",
  dragon:   "1485468518597263450",
  neutral:  "1485468519771541695",
};

function elementIconUrl(el) {
  const id = ELEMENT_EMOJI_IDS[(el || "").toLowerCase()];
  return id ? `https://cdn.discordapp.com/emojis/${id}.png?size=64` : null;
}

const dismissedKey = (id) => `raid-banner-dismissed:${id}`;

export function RaidBanner() {
  const [raids, setRaids] = useState([]);
  const [idx, setIdx] = useState(0);
  const [dismissedIds, setDismissedIds] = useState(() => {
    // Hydrate from sessionStorage — banners stay dismissed for the
    // current tab but come back on reload.
    if (typeof sessionStorage === "undefined") return new Set();
    const out = new Set();
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith("raid-banner-dismissed:")) {
        out.add(key.replace("raid-banner-dismissed:", ""));
      }
    }
    return out;
  });
  const navigate = useNavigate();

  // Periodic refresh — uses the public unauth endpoint so logged-out
  // visitors also see the banner (community-grow hook).
  const fetchRaids = useCallback(async () => {
    try {
      const { data } = await api.get("/raids/active");
      setRaids(Array.isArray(data?.raids) ? data.raids : []);
    } catch {
      // Silent — banner just stays hidden if the endpoint is unreachable
    }
  }, []);

  useEffect(() => {
    fetchRaids();
    const t = setInterval(fetchRaids, POLL_MS);
    return () => clearInterval(t);
  }, [fetchRaids]);

  // Filter out dismissed raids
  const visible = useMemo(
    () => raids.filter((r) => !dismissedIds.has(r.id)),
    [raids, dismissedIds],
  );

  // Cycle through multiple active raids so neither server is hidden.
  useEffect(() => {
    if (visible.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % visible.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [visible.length]);

  // Keep `idx` in bounds when the visible list shrinks (e.g. one raid
  // ended, or the user dismissed one).
  useEffect(() => {
    if (idx >= visible.length && visible.length > 0) setIdx(0);
  }, [visible.length, idx]);

  if (visible.length === 0) return null;
  const raid = visible[idx % visible.length];
  const style = CATEGORY_STYLES[raid.category] || CATEGORY_STYLES.combat;

  const onDismiss = (e) => {
    e.stopPropagation();
    sessionStorage.setItem(dismissedKey(raid.id), "1");
    setDismissedIds((s) => new Set(s).add(raid.id));
  };

  const onClick = () => {
    // Drop the user on the LiveMap with the raid's coords highlighted.
    // We pass the raid id as a query param so the map can auto-focus.
    navigate(`/map?raid=${encodeURIComponent(raid.id)}`);
  };

  return (
    <div
      role="alert"
      onClick={onClick}
      className={
        "relative w-full text-white cursor-pointer overflow-hidden " +
        "border-b border-white/10 shadow-lg " +
        "bg-gradient-to-r " + style.color
      }
      data-testid="raid-banner"
    >
      {/* Animated pulse stripe — pure CSS, no JS animation cost. */}
      <span className="absolute inset-y-0 left-0 w-1 bg-white/40 animate-pulse" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
        {raid.pal_image ? (
          <img
            src={raid.pal_image}
            alt=""
            className="w-9 h-9 rounded-full border border-white/30 object-cover flex-shrink-0"
            loading="lazy"
          />
        ) : (
          <div className="w-9 h-9 rounded-full bg-black/30 grid place-items-center flex-shrink-0">
            <Swords size={16} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest opacity-90 font-bold flex items-center gap-1.5">
            {(() => {
              const url = elementIconUrl(raid.element);
              return url ? (
                <img
                  src={url}
                  alt={raid.element}
                  className="w-4 h-4 inline-block"
                  loading="lazy"
                  draggable={false}
                />
              ) : (
                <span>{style.chip}</span>
              );
            })()}
            <span>Active Raid Boss · {style.label}</span>
          </div>
          <div className="text-sm truncate font-medium">
            <span className="font-bold" data-testid={`raid-banner-pal-${raid.id}`}>
              Lv {raid.level} {raid.pal_name}
            </span>
            <span className="opacity-90"> spawned on </span>
            <span className="font-bold">{raid.server}</span>
            <span className="opacity-70 hidden sm:inline">
              {" "}— {raid.location_name}
            </span>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-1 text-xs uppercase tracking-wider opacity-90 font-bold">
          <MapPin size={12} /> View on map
        </div>
        {visible.length > 1 && (
          <div
            className="hidden sm:block text-[10px] uppercase tracking-wider opacity-60 font-mono"
            data-testid="raid-banner-counter"
          >
            {idx + 1}/{visible.length}
          </div>
        )}
        <button
          type="button"
          onClick={onDismiss}
          className="p-1.5 rounded-md hover:bg-white/10 transition-colors flex-shrink-0"
          aria-label="Dismiss raid banner"
          data-testid="raid-banner-dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
